import {MissingSentryProjectObservabilityError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import * as reportsServer from 'src/internal/errors/reports.server';
import buildErrorScope from 'src/internal/errors/utils/buildErrorScope';
import generateId from 'src/internal/errors/utils/generateId';
import withSdkSignature from 'src/internal/errors/utils/withSdkSignature';

import type {SentryHub} from 'src/internal/errors/sentry/types';
import type {ErrorScope, IReports, Severity} from 'src/internal/errors/types';
import type ReportsServer from 'src/internal/errors/utils/ReportsServer';
import type {ObservabilityConfig} from 'src/types';

export class Reports implements IReports {
  #id;
  #config: ObservabilityConfig;
  #server?: ReportsServer;

  constructor(config: ObservabilityConfig) {
    this.#config = config;
    this.#id = generateId(this.#config);

    if (
      config.errors?._internal_injected_methods_only ||
      config.errors?.disabled ||
      config.errors?.reporter
    ) {
      return;
    }

    if (!this.#config.errors?.projects?.[this.#config.project]) {
      logErrorInDev(
        new MissingSentryProjectObservabilityError(this.#config.project),
      );
    }

    try {
      this.#server = reportsServer.initReports(this.#id, config);
    } catch (error) {
      // The errors here are unexpected. But if they happen, degrade gracefully:
      // - Don't crash the code calling @sail/observability APIs
      // - Report an unhandled exception so that it is reported to Sentry
      Promise.reject(error);
    }
  }

  public error = (error: any, scope?: ErrorScope): void => {
    this.report('error', error, scope);
  };

  public warning = (error: any, scope?: ErrorScope): void => {
    this.report('warning', error, scope);
  };

  public getMainHub = (): SentryHub | undefined => {
    return this.#server?.getMainHub();
  };

  private report(
    level: Exclude<Severity, 'info'>,
    error: any,
    scope?: ErrorScope,
  ): void {
    logErrorInDev(error, level, scope);

    if (this.#config.errors?.disabled) {
      return;
    }

    if (
      scope?.project &&
      !this.#config.errors?.projects?.[scope.project] &&
      !this.#config.errors?.reporter
    ) {
      logErrorInDev(new MissingSentryProjectObservabilityError(scope.project));
    }

    const injectedMethod =
      this.#config._internal_observability?.errors?.[level];

    if (injectedMethod) {
      const errorScope = buildErrorScope(this.#config, error, scope);

      injectedMethod(error, withSdkSignature(errorScope));
      return;
    }

    if (this.#config.errors?.reporter) {
      /**
       * If a reporter is provided, we will use it instead of the default @sentry/browser reporter.
       *
       * Use cases:
       * - React Native: pass in the @sentry/react-native reporter
       *
       * `frontend.observability.*` analytics events and metrics will not be reported,
       * because custom reporter doesn't have SailObservabilityIntegration.
       */
      const errorScope = buildErrorScope(this.#config, error, scope);
      this.#config.errors.reporter[level](error, errorScope);
      return;
    }

    reportsServer.report(level, this.#id, error, ...[scope].filter(Boolean));
  }
}
